import React from 'react';

function SampleModel({ 
  header = "",  // Add default values
  body = "", 
  footer = "",
  variableSamples, 
  setVariableSamples,
  extractVariables,
  generatePreviewText
}) {
  // Enhanced extractVariables function with null/undefined check
  const safeExtractVariables = (text) => {
    if (!text || typeof text !== 'string') {
      return [];
    }
    const matches = text.match(/{{(.*?)}}/g) || [];
    return matches.map(match => match.slice(2, -2).trim());
  };

  // Get all variables from all fields with safe extraction
  const headerVariables = safeExtractVariables(header);
  const bodyVariables = safeExtractVariables(body);
  const footerVariables = safeExtractVariables(footer);
  
  const allVariables = [
    ...headerVariables.map(v => ({ field: 'header', variable: v })),
    ...bodyVariables.map(v => ({ field: 'body', variable: v })),
    ...footerVariables.map(v => ({ field: 'footer', variable: v }))
  ];

  // Don't render if no variables
  if (allVariables.length === 0) {
    return null;
  }

  const handleSampleChange = (field, variable, value) => {
    const key = `${field}-${variable}`;
    setVariableSamples(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const getSampleValue = (field, variable) => {
    const key = `${field}-${variable}`;
    return variableSamples?.[key] || '';
  };

  const hasEmptySamples = allVariables.some(({ field, variable }) => 
    !getSampleValue(field, variable)
  );


  return (
    <div className="border rounded-lg bg-gray-50 overflow-hidden">
      <div className="p-4 border-b bg-white">
        <h3 className="text-lg font-semibold mb-2">Variable samples</h3>
        <p className="text-sm text-gray-600">
          Include samples of all variables in your message to help Meta review your template. 
          Remember not to include any customer information to protect your customer's privacy.
        </p>
      </div>

      <div className="p-4 space-y-6">
        {/* Header Variables */}
        {headerVariables.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Header Variables</h4>
            <div className="space-y-2">
              {headerVariables.map((variable, index) => (
                <div key={`header-${index}`} className="flex gap-3 items-center">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      className="px-3 py-2 border rounded bg-gray-100 text-gray-600 text-sm"
                      value={`{{${variable}}}`}
                      disabled
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border rounded focus:outline-none focus:border-blue-500 text-sm"
                      placeholder={`Sample for ${variable}`}
                      value={getSampleValue('header', variable)}
                      onChange={(e) => handleSampleChange('header', variable, e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
            {headerVariables.some(v => !getSampleValue('header', v)) && (
              <p className="text-xs text-red-500 mt-2">Add sample text for all header variables</p>
            )}
          </div>
        )}

        {/* Body Variables */}
        {bodyVariables.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Body Variables</h4>
            <div className="space-y-2">
              {bodyVariables.map((variable, index) => (
                <div key={`body-${index}`} className="flex gap-3 items-center">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      className="px-3 py-2 border rounded bg-gray-100 text-gray-600 text-sm"
                      value={`{{${variable}}}`}
                      disabled
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border rounded focus:outline-none focus:border-blue-500 text-sm"
                      placeholder={`Sample for ${variable}`}
                      value={getSampleValue('body', variable)}
                      onChange={(e) => handleSampleChange('body', variable, e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
            {bodyVariables.some(v => !getSampleValue('body', v)) && (
              <p className="text-xs text-red-500 mt-2">Add sample text for all body variables</p>
            )}
          </div>
        )}

        {/* Footer Variables */}
        {footerVariables.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Footer Variables</h4>
            <div className="space-y-2">
              {footerVariables.map((variable, index) => (
                <div key={`footer-${index}`} className="flex gap-3 items-center">
                  <div className="flex-1 grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      className="px-3 py-2 border rounded bg-gray-100 text-gray-600 text-sm"
                      value={`{{${variable}}}`}
                      disabled
                    />
                    <input
                      type="text"
                      className="px-3 py-2 border rounded focus:outline-none focus:border-blue-500 text-sm"
                      placeholder={`Sample for ${variable}`}
                      value={getSampleValue('footer', variable)}
                      onChange={(e) => handleSampleChange('footer', variable, e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>
            {footerVariables.some(v => !getSampleValue('footer', v)) && (
              <p className="text-xs text-red-500 mt-2">Add sample text for all footer variables</p>
            )}
          </div>
        )}


      </div>
    </div>
  );
}

export default SampleModel;